import { type ReactElement } from "react";
import { useLocation } from "react-router-dom";
import { useAuthenticated } from "@/frontend/shared/lib/hooks/useAuthenticated";
import {
  getRouteDashboard,
  getRouteExpertRootPage,
  getRouteFounderCompanyProfile,
  getRouteSignIn
} from "@/frontend/shared/const/router";
import { useHistoricalNavigate } from "@/frontend/shared/lib/useHistoricalNavigate/useHistoricalNavigate";
import { useMe } from "@/frontend/shared/lib/hooks/useMe";
import { ERoleAccess } from "../config/routeConfig";
import { Loader } from "@/frontend/shared/ui/Loader/Loader";
import { ImpersonationSessionWidget } from "@/frontend/components/ImpersonationThing/ImpersonationThing";
import styles from "./ProtectedRoute.module.scss";

type TProps = {
  children: ReactElement;
  roleAccess?: ERoleAccess;
};

export function ProtectedRoute({ children, roleAccess }: TProps) {
  const { navigate } = useHistoricalNavigate();
  const { data: isAuth, isLoading, refetch } = useAuthenticated();
  const { data: userData } = useMe();
  const { pathname, search } = useLocation();

  const isUserFounderOnWrongPage =
    roleAccess !== userData?.entity && userData?.entity === ERoleAccess.FOUNDER && roleAccess !== ERoleAccess.ALL;

  const isUserInvestorOnWrongPage =
    roleAccess !== userData?.entity && userData?.entity === ERoleAccess.INVESTOR && roleAccess !== ERoleAccess.ALL;

  const isUserExpertOnWrongPage =
    roleAccess !== userData?.entity && userData?.entity === ERoleAccess.EXPERT && roleAccess !== ERoleAccess.ALL;

  if (isLoading) {
    return (
      <div className={styles.loaderContainer}>
        <Loader />
      </div>
    );
  }

  if (!isAuth) {
    const nextNav = pathname + search;
    navigate(getRouteSignIn() + `?next=${encodeURIComponent(nextNav)}`);
    return null;
  }

  if (isUserFounderOnWrongPage) {
    navigate(getRouteFounderCompanyProfile(), { replace: true });
    return null;
  }

  if (isUserInvestorOnWrongPage) {
    navigate(getRouteDashboard(), { replace: true });
    return null;
  }

  if (isUserExpertOnWrongPage) {
    navigate(getRouteExpertRootPage(), { replace: true });
    return null;
  }

  return (
    <div>
      {children}
      {Boolean(userData?.impersonatorId) ? (
        <ImpersonationSessionWidget email={userData?.email} refetch={refetch} />
      ) : null}
    </div>
  );
}
